/**
 * The Work Order's field side (piece 29): the calendar entry an assignment
 * writes, the checklist a line's Asset offers, the template's informational
 * worker count, the on-site figure the check-in pairs add up to, when a Deal's
 * Work in Progress is done, and the line editor's one text box. Pure: the
 * services read the rows and this says what they add up to.
 */

/**
 * 🔒 The worker's calendar entry for one line is keyed on the line and the
 * employment, in the Event's own `uid`, so no column names it: moving the
 * line's due date finds it again, and removing the assignment finds it to take
 * it off the calendar.
 */
export function lineEventUid(lineId: string, employmentId: string): string {
  return `work-order-line-${lineId}-${employmentId}`;
}

/** Each (employment, day) an assignment and a due date put on a worker's calendar; a line with no due date puts nothing there. */
export function assignmentEntries(line: { id: string; dueDate: string | null; employmentIds: readonly string[] }): { uid: string; employmentId: string; day: string }[] {
  if (!line.dueDate) return [];
  return line.employmentIds.map((employmentId) => ({ uid: lineEventUid(line.id, employmentId), employmentId, day: line.dueDate! }));
}

/**
 * 🔒 A line's Asset offers its Category's templates. The Category is the
 * `categoryId` of the Item the nearest Component-linked node links through
 * `AssetComponent`, walking up from the line's own node, so a Grouping reads
 * its nearest Component ancestor's. `ancestry` is the node first, then each
 * parent up to the top; null where no node on the way names a Category.
 */
export function lineCategoryOf(ancestry: readonly { nodeId: string; categoryId: string | null }[]): string | null {
  for (const node of ancestry) if (node.categoryId) return node.categoryId;
  return null;
}

export interface MinimumWorkersView {
  minimum: number | null;
  assigned: number;
  /** True where the template asks for more than are assigned: a warning, never a refusal. */
  short: boolean;
}

/** 🔨 A template's Minimum Workers is informational and never blocks: the count is drawn as a confirmation or a warning, and dispatch reads nothing here. */
export function minimumWorkersView(minimum: number | null, assigned: number): MinimumWorkersView {
  return { minimum, assigned, short: minimum !== null && assigned < minimum };
}

/**
 * 🔒 A Work Order's actual on-site time is the sum of its workers' check-in
 * pairs, one arrival and one departure each (decision 0229). An open pair has
 * no departure yet and adds nothing, and the figure is never derived from the
 * labour hours beside it.
 */
export function onSiteHours(pairs: readonly { checkedInAt: Date; checkedOutAt: Date | null }[]): number {
  const ms = pairs.reduce((sum, pair) => sum + (pair.checkedOutAt ? Math.max(0, pair.checkedOutAt.getTime() - pair.checkedInAt.getTime()) : 0), 0);
  return Math.round((ms / 3_600_000) * 100) / 100;
}

/**
 * 🔒 The Deal's Work in Progress completes when the whole follow-up chain has
 * closed, never when the first Work Order does: every Work Order on the Deal is
 * Closed or Void, and at least one of them Closed.
 */
export function workInProgressDone(statuses: readonly string[]): boolean {
  const live = statuses.filter((status) => status !== 'Void');
  return live.length > 0 && live.every((status) => status === 'Closed');
}

/**
 * The line editor's one text (review item T141): the grid's name box and the
 * notes box under it are the line's description, the first line of it and the
 * rest, so a line typed on the edit page and one typed on the Details view read
 * as the same line.
 */
export function lineEditorText(label: string, notes: string): string {
  const head = label.trim();
  const tail = notes.trim();
  if (!head) return tail;
  return tail ? `${head}\n${tail}` : head;
}

export function lineEditorParts(description: string): { label: string; notes: string } {
  const at = description.indexOf('\n');
  if (at < 0) return { label: description, notes: '' };
  return { label: description.slice(0, at), notes: description.slice(at + 1) };
}
